/**
 * Shared GLSL fragments.
 *
 * Every post pass is a `RawShaderMaterial` at `GLSL3`, so three injects nothing
 * beyond `#version 300 es`. We declare precision, attributes and outputs
 * ourselves. That keeps the generated source identical to what is written here,
 * which matters when a pass is being tuned against a reference still.
 */

/**
 * One oversized triangle in clip space. Cheaper than a quad (no diagonal seam,
 * one less vertex, better quad utilisation on the raster) and it lets the
 * vertex stage skip every matrix entirely.
 *
 * `vUv` runs 0..2 across the triangle; the on-screen third covers exactly 0..1.
 */
export const FULLSCREEN_VERT = /* glsl */ `
precision highp float;

in vec3 position;
out vec2 vUv;

void main() {
  vUv = position.xy * 0.5 + 0.5;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

/** Luminance, ACES tonemap, sRGB transfer and a cheap hash. */
export const GLSL_COLOR = /* glsl */ `
float luma(vec3 c) {
  return dot(c, vec3(0.2126, 0.7152, 0.0722));
}

// Narkowicz's ACES fit, on one scalar.
float acesScalar(float x) {
  const float a = 2.51;
  const float b = 0.03;
  const float c = 2.43;
  const float d = 0.59;
  const float e = 0.14;
  return clamp((x * (a * x + b)) / (x * (c * x + d) + e), 0.0, 1.0);
}

// Hue-preserving tonemap. Curve the peak channel, carry the chromaticity
// through unchanged, then bleed toward white only as a function of how far past
// the shoulder we are.
//
// Applying the fit per channel instead compresses each channel toward the same
// asymptote, and saturation IS the ratio between channels, so every emitter
// above ~1.5 collapsed to cream. A 3.9x yellow swarmling rendered at 15%
// saturation; Geometry Wars renders the same emitter at 84%.
//
// The wash term is deliberate, not a leftover: GW does go white at its hottest
// cores. It takes 6x overbright to get there, not 2x.
vec3 acesFilm(vec3 x) {
  float peak = max(x.r, max(x.g, x.b));
  if (peak < 1e-5) return vec3(0.0);
  vec3 ratio = x / peak;
  float tm = acesScalar(peak);
  float wash = clamp((peak - 2.0) / 4.0, 0.0, 1.0);
  wash *= wash;
  ratio = mix(ratio, vec3(1.0), wash * 0.85);
  return clamp(ratio * tm, 0.0, 1.0);
}

vec3 linearToSrgb(vec3 c) {
  vec3 lo = c * 12.92;
  vec3 hi = 1.055 * pow(max(c, vec3(1e-5)), vec3(1.0 / 2.4)) - 0.055;
  return mix(lo, hi, step(vec3(0.0031308), c));
}

float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
`;

/**
 * Thirteen-tap downsample (Jimenez, Call of Duty AW). Four inner taps carry
 * half the weight, which is what removes the pulsing you get from a naive box
 * filter when a bright sprite crosses a texel boundary.
 *
 * `uTexel` is the *source* texel size. `FETCH(uv)` is supplied by the caller so
 * the bright pass can prefilter each tap individually.
 */
export const GLSL_TAP13 = /* glsl */ `
vec3 tap13(vec2 uv, vec2 texel) {
  vec3 a = FETCH(uv + texel * vec2(-2.0, -2.0));
  vec3 b = FETCH(uv + texel * vec2( 0.0, -2.0));
  vec3 c = FETCH(uv + texel * vec2( 2.0, -2.0));

  vec3 d = FETCH(uv + texel * vec2(-2.0,  0.0));
  vec3 e = FETCH(uv);
  vec3 f = FETCH(uv + texel * vec2( 2.0,  0.0));

  vec3 g = FETCH(uv + texel * vec2(-2.0,  2.0));
  vec3 h = FETCH(uv + texel * vec2( 0.0,  2.0));
  vec3 i = FETCH(uv + texel * vec2( 2.0,  2.0));

  vec3 j = FETCH(uv + texel * vec2(-1.0, -1.0));
  vec3 k = FETCH(uv + texel * vec2( 1.0, -1.0));
  vec3 l = FETCH(uv + texel * vec2(-1.0,  1.0));
  vec3 m = FETCH(uv + texel * vec2( 1.0,  1.0));

  return e * 0.125
       + (a + c + g + i) * 0.03125
       + (b + d + f + h) * 0.0625
       + (j + k + l + m) * 0.125;
}
`;
